import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Space, Typography, Table, Tag, message, DatePicker, Row, Col, Statistic, Empty } from 'antd';
import { ReloadOutlined, BarChartOutlined } from '@ant-design/icons';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';
import dayjs from 'dayjs';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  success: '#10B981',
  warning: '#F59E0B',
};

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
  
  @media (max-width: 768px) {
    padding: 0 8px;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const FiltersContainer = styled.div`
  background: ${COLORS.bgSecondary};
  border: 1px solid ${COLORS.border};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
`;

const StatsCard = styled(Card)`
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  
  .ant-statistic-title {
    color: ${COLORS.textSecondary} !important;
  }
  
  .ant-statistic-content {
    color: ${COLORS.textPrimary} !important;
  }
`;

const BillingReports: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [monthlyReport, setMonthlyReport] = useState<any[]>([]);
  const [fullReport, setFullReport] = useState<any>(null);

  const fetchMonthlyReport = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (dateRange) {
        params.startDate = dateRange[0].format('YYYY-MM-DD');
        params.endDate = dateRange[1].format('YYYY-MM-DD');
      }

      const response = await apiClient.get(
        `${API_ENDPOINTS.ADMIN_GET_MONTHLY_REVENUE_REPORT || '/api/Administrator/billing/reports/monthly-revenue'}`,
        { params }
      );

      if (response.data?.status === 'success' && response.data?.data) {
        setMonthlyReport(response.data.data);
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to fetch monthly revenue report');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchMonthlyReport();
  }, [fetchMonthlyReport]);

  const fetchFullReport = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (dateRange) {
        params.startDate = dateRange[0].format('YYYY-MM-DD');
        params.endDate = dateRange[1].format('YYYY-MM-DD');
      }

      const response = await apiClient.get(
        `${API_ENDPOINTS.ADMIN_GET_FULL_BILLING_REPORT || '/api/Administrator/billing/reports/full'}`,
        { params }
      );

      if (response.data?.status === 'success' && response.data?.data) {
        setFullReport(response.data.data);
      }
    } catch (error: any) {
      message.error('Failed to fetch full billing report');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const monthlyColumns = [
    {
      title: 'Month',
      dataIndex: 'month',
      key: 'month',
      render: (month: string) => dayjs(month).format('MMMM YYYY'),
    },
    {
      title: 'Total Revenue',
      dataIndex: 'totalRevenue',
      key: 'totalRevenue',
      render: (amount: number) => (
        <Text strong style={{ color: COLORS.success }}>
          ${parseFloat(amount.toString()).toFixed(2)}
        </Text>
      ),
      sorter: (a: any, b: any) => a.totalRevenue - b.totalRevenue,
    },
    {
      title: 'Total Invoices',
      dataIndex: 'totalInvoices',
      key: 'totalInvoices',
    },
    {
      title: 'Paid',
      dataIndex: 'paidInvoices',
      key: 'paidInvoices',
      render: (count: number) => <Tag color="success">{count}</Tag>,
    },
    {
      title: 'Pending',
      dataIndex: 'pendingInvoices',
      key: 'pendingInvoices',
      render: (count: number) => <Tag color="warning">{count}</Tag>,
    },
    {
      title: 'Failed',
      dataIndex: 'failedInvoices',
      key: 'failedInvoices',
      render: (count: number) => <Tag color="error">{count}</Tag>,
    },
    {
      title: 'Avg Invoice',
      dataIndex: 'averageInvoiceAmount',
      key: 'averageInvoiceAmount',
      render: (amount: number) => `$${parseFloat(amount.toString()).toFixed(2)}`,
    },
  ];

  const topMerchantsColumns = [
    {
      title: 'Merchant',
      dataIndex: 'merchantName',
      key: 'merchantName',
    },
    {
      title: 'Total Paid',
      dataIndex: 'totalPaid',
      key: 'totalPaid',
      render: (amount: number) => (
        <Text strong style={{ color: COLORS.success }}>
          ${parseFloat(amount.toString()).toFixed(2)}
        </Text>
      ),
      sorter: (a: any, b: any) => a.totalPaid - b.totalPaid,
    },
    {
      title: 'Invoice Count',
      dataIndex: 'invoiceCount',
      key: 'invoiceCount',
    },
  ];

  const planDistributionColumns = [
    {
      title: 'Plan',
      dataIndex: 'planDisplayName',
      key: 'planDisplayName',
    },
    {
      title: 'Subscriptions',
      dataIndex: 'subscriptionCount',
      key: 'subscriptionCount',
    },
    {
      title: 'Total Revenue',
      dataIndex: 'totalRevenue',
      key: 'totalRevenue',
      render: (amount: number) => (
        <Text strong style={{ color: COLORS.success }}>
          ${parseFloat(amount.toString()).toFixed(2)}
        </Text>
      ),
    },
  ];

  return (
    <PageContainer>
      <HeaderContainer>
        <Title level={2} style={{ color: COLORS.textPrimary, margin: 0 }}>
          Billing Reports
        </Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchMonthlyReport}>
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<BarChartOutlined />}
            onClick={fetchFullReport}
            style={{ backgroundColor: COLORS.accent, borderColor: COLORS.accent }}
          >
            Generate Full Report
          </Button>
        </Space>
      </HeaderContainer>

      <FiltersContainer>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text strong style={{ color: COLORS.textPrimary }}>Date Range (Optional)</Text>
          <RangePicker
            style={{ width: '100%', maxWidth: 400 }}
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                setDateRange([dayjs(dates[0].toDate()), dayjs(dates[1].toDate())]);
              } else {
                setDateRange(null);
              }
            }}
            format="YYYY-MM-DD"
          />
        </Space>
      </FiltersContainer>

      {fullReport && (
        <Card
          style={{
            background: COLORS.bgSecondary,
            border: `1px solid ${COLORS.border}`,
            marginBottom: 24,
          }}
        >
          <Title level={3} style={{ color: COLORS.textPrimary, marginBottom: 24 }}>
            Full Billing Report
          </Title>
          
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} md={6}>
              <StatsCard>
                <Statistic
                  title="Total Revenue"
                  value={fullReport.summary?.totalRevenue || 0}
                  prefix="$"
                  precision={2}
                  valueStyle={{ color: COLORS.success }}
                />
              </StatsCard>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <StatsCard>
                <Statistic
                  title="Total Invoices"
                  value={fullReport.summary?.totalInvoices || 0}
                />
              </StatsCard>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <StatsCard>
                <Statistic
                  title="Active Subscriptions"
                  value={fullReport.summary?.activeSubscriptions || 0}
                  valueStyle={{ color: COLORS.accent }}
                />
              </StatsCard>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <StatsCard>
                <Statistic
                  title="Paid Invoices"
                  value={fullReport.summary?.paidInvoices || 0}
                  valueStyle={{ color: COLORS.success }}
                />
              </StatsCard>
            </Col>
          </Row>

          <Title level={4} style={{ color: COLORS.textPrimary, marginTop: 32, marginBottom: 16 }}>
            Top Merchants by Revenue
          </Title>
          <Table
            columns={topMerchantsColumns}
            dataSource={fullReport.topMerchants || []}
            rowKey="merchantCompanyId"
            pagination={false}
            style={{ marginBottom: 24 }}
            locale={{
              emptyText: <Empty description="No merchant data" />,
            }}
          />

          <Title level={4} style={{ color: COLORS.textPrimary, marginTop: 32, marginBottom: 16 }}>
            Plan Distribution
          </Title>
          <Table
            columns={planDistributionColumns}
            dataSource={fullReport.planDistribution || []}
            rowKey="planName"
            pagination={false}
            locale={{
              emptyText: <Empty description="No plan data" />,
            }}
          />
        </Card>
      )}

      <Card
        style={{
          background: COLORS.bgSecondary,
          border: `1px solid ${COLORS.border}`,
        }}
      >
        <Title level={3} style={{ color: COLORS.textPrimary, marginBottom: 24 }}>
          Monthly Revenue Breakdown
        </Title>
        <Table
          columns={monthlyColumns}
          dataSource={monthlyReport}
          rowKey="month"
          loading={loading}
          pagination={{
            pageSize: 12,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} months`,
          }}
          locale={{
            emptyText: <Empty description="No revenue data available" />,
          }}
        />
      </Card>
    </PageContainer>
  );
};

export default BillingReports;
